import { authService } from '@/services/auth-service';
import { isNull, isStringNull } from '@/shared/utils/utils';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import uuid from 'uuid/v4';
import { environment } from './environment';
import { EventBus, HttpErrorEvent } from './event-bus';
import { createApplicationError } from '@/models/errors/application-error-factory';

declare module 'axios' {
    interface AxiosRequestConfig {
        responseMapper?: MapResponseHandler<any>
    }
}

export class HttpService {
    public static readonly IdentityMapping = data => data;

    public static readonly SessionId = uuid();
    private http: AxiosInstance = null;
    private resourceUrl = null
    private responseMapper: MapResponseHandler<any> = null
    private processResultFunc: (result: any) => any = null

    protected getMailWizzInstanceId() : number {
        return 0
    }

    protected getResourceUrl(): string {
        if (isNull(this.resourceUrl)) {
            throw new Error(`Argument 'resourceUrl' is NULL.`)
        }

        return this.resourceUrl
    }

    protected getResponseMapper(): MapResponseHandler<any> {
        return this.responseMapper
    }

    protected processResult(result: any): any {
        return this.processResultFunc(result)
    }

    constructor(resourceUrl?: string, responseMapper?: MapResponseHandler<any>, processResult?: (result: any) => any) {
        this.http = axios.create({
            headers: {
                'SessionId': HttpService.SessionId,
                'Content-Type': 'application/json; charset=utf-8',
            },
        })

        this.http.interceptors.request.use(this.addRequestIdHeaderInterceptor)
        this.http.interceptors.request.use(this.addAuthorizationHeaderInterceptor)

        this.http.interceptors.response.use(
            r => this.mapResponse(r),
            e => this.handleError(e))

        this.resourceUrl = resourceUrl
        this.responseMapper = responseMapper || HttpService.IdentityMapping
        this.processResultFunc = processResult || HttpService.IdentityMapping
    }

    getAll<T>(request?: IRequest<T>): Promise<T[]> {
        return this.sendRequest('get', request)
    }

    getPaged<T>(request?: IPagedRequest<T>): Promise<IPagedResponse<T>> {
        return this.sendRequest('get', request)
    }

    get<T>(request?: IRequest<T>): Promise<T> {
        return this.sendRequest('get', request)
    }

    getFile(request?: IFileRequest): Promise<IFileResponse> {
        return this.sendRequest('get', {
            ...request,
            responseType: 'blob',
        })
    }

    getTotalCount(request?: IPagedRequest<number>): Promise<number> {
        return this.sendRequest('get', request)
    }

    /* TODO: rename */
    getFileByPost(request?: IFileRequest): Promise<IFileResponse> {
        return this.sendRequest('post', {
            ...request,
            responseType: 'blob',
        })
    }

    create<T>(request?: IRequest<T>): Promise<T> {
        return this.sendRequest('post', request)
    }

    post<T>(request?: IRequest<T>): Promise<T> {
        return this.sendRequest('post', request)
    }

    update<T>(request?: IRequest<T>): Promise<T> {
        return this.sendRequest('put', request)
    }

    delete<T>(request?: IRequest<T>): Promise<T> {
        return this.sendRequest('delete', request)
    }

    protected getBaseApiUrl(): Promise<any> {
        return environment.getBaseApiUrl();
    }

    private sendRequest<T>(type: string, request?: IRequest<T>) {
        const config = Object.assign({
            responseType: 'json',
            responseMapper: null,
            processResult: null,
            data: null,
            params: null,
            headers: null,
        }, request)

        const mailWizzInstanceId = this.getMailWizzInstanceId()
        if (mailWizzInstanceId > 0) {
            config.params = Object.assign(config.params || {}, { mailWizzInstanceId })
        }
        return this.getBaseApiUrl()
            .then(baseUrl => this.http
                .request( {
                    baseURL: baseUrl,
                    method: type,
                    url: this.buildUrl(request),
                    params: config.params,
                    data: config.data,
                    responseType: config.responseType,
                    responseMapper: config.responseMapper,
                    processResult: config.processResult,
                    headers: config.headers,
                } as any) as any)
    }

    private mapResponse(response: AxiosResponse) {
        this.logResponse(response)

        if (/DELETE/i.test(response.config.method)) {
            return response.data
        }

        if (response.config.responseType === 'blob') {
            return this.mapFile(response)
        }
        const mapper = response.config.responseMapper || this.getResponseMapper() || HttpService.IdentityMapping
        const processResult = ( response.config as any).processResult || this.processResult
        if (response.config.responseType !== 'json') {
            return mapper(response.data)
        }

        if (this.isPagedResponse(response.data)) {
            return {
                data: this.mapResponseData(response.data.data, mapper, processResult),
                total: response.data.total,
            }
        }

        return this.mapResponseData(response.data, mapper, processResult)
    }

    private mapResponseData(data: any, mapper: MapResponseHandler<any>, processResult: MapResponseHandler<any>): any {
        let mappedResult: any
        if (Array.isArray(data)) {
            mappedResult = data.map(x => {
                if (isNaN(x)) return mapper(x);
                return x;
            })
        } else {
            if (isNaN(data)) { // data is not a number
                mappedResult = mapper(data)
            } else {
                mappedResult = data // not using mapper for number values
            }
        }
        const finalResult = processResult.call(this, mappedResult)

        return finalResult
    }

    private isPagedResponse<T = any>(response: any): response is IPagedResponse<T> {
        return !isNull(response)
            && (!isNull(response.total) && !isNull(response.data) && Array.isArray(response.data))
    }

    private addRequestIdHeaderInterceptor(config: AxiosRequestConfig) {
        config.headers = config.headers || {}
        Object.assign(config.headers, { RequestId: uuid() })
        return config
    }

    private addAuthorizationHeaderInterceptor(config: AxiosRequestConfig) {
        if (isNull(authService.token)) {
            return config
        }

        config.headers = config.headers || {}
        Object.assign(config.headers, { Authorization: `Bearer ${authService.token}` })
        return config
    }

    private logResponse(response: AxiosResponse) {
        // journal.log({
        //     Name: "client.http.response",
        //     Url: response.request.url,
        //     Status: response.status,
        //     RequestId: response.headers["RequestId"],
        //     TimeStamp: new Date().toUTCString()
        // });

        return response
    }

    private async handleError(error: AxiosError) {
        const e = await createApplicationError(error)
        EventBus.$emit(HttpErrorEvent, e)

        return Promise.reject(e)
    }

    private mapFile(response: AxiosResponse): IFileResponse {
        const type = !isNull(response.data) ? response.data.type || '' : '';
        return {
            content: new Blob([response.data], { type }),
            fileName: this.extractFileName(response),
        };
    }

    private extractFileName(response: AxiosResponse) {
        const disposition: string = response.headers['content-disposition'];
        if (disposition && disposition.indexOf('attachment') !== -1) {
            const filenameRegex: RegExp = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches: RegExpExecArray | null = filenameRegex.exec(disposition);
            if (matches != null && matches[1]) {
                return matches[1].replace(/['"]/g, '');
            }
        }
    }

    /* Result URL: {resourceBaseUrl}/${resourceId}/${path}?{params} */
    private buildUrl(request?: IRequest<any>): string {
        let url = request && !isStringNull(request.resourceBaseUrl)
            ? request.resourceBaseUrl
            : this.getResourceUrl()

        url = request && !isNull(request.resourceId)
            ? `${url}/${request.resourceId}`
            : url

        url = request && !isStringNull(request.path)
            ? `${url}/${request.path}`
            : url

        url = url.replace(/\/+/gm, '/')

        return url
    }
}

export interface IRequest<T> {
    params?: any | URLSearchParams
    responseMapper?: MapResponseHandler<T>
    data?: any
    path?: string
    resourceBaseUrl?: string
    resourceId?: any
    responseType?: string
    processResult?: MapResponseHandler<T>
    headers?: any
}

export interface IFileRequest {
    params?: any | URLSearchParams
    data?: any
    path?: string
    resourceBaseUrl?: string
    resourceId?: any
}

export interface IFileResponse {
    content: Blob
    fileName: string
}

export interface IPagedRequest<T> {
    params?: any | URLSearchParams
    responseMapper?: MapResponseHandler<T>
    data?: any
    path?: string
    resourceBaseUrl?: string
    resourceId?: any
}

export interface IPagedResponse<T> {
    data: T[];
    total: any
}

export type MapResponseHandler<T> = (json: any) => T
